// src/components/templates/templateTokens.js
//
// Design tokens for the Template Gallery.
//
// It re-exports `SP`, `FONT` and `BP` from the Manuscript Structure page's
// tokens rather than restating them — the same thing `corkTokens.js` does — so
// there is one place where the prototype palette was mapped onto the app's
// primary and secondary, and one place it can change. Read the header of
// `structureTokens.js` for that mapping.
//
// **On the category colours.** The gallery prototype gave its seven categories
// seven hues — a teal, a rose, an amber, a sage. Those are not the app's
// colours, and the app has two: a purple and a gold. Seven distinguishable hues
// cannot be derived from two without inventing five, which `src/CLAUDE.md` says
// to flag rather than do quietly. So the values below are all existing tokens,
// and the category is carried by its **label**, which every card and filter
// button shows. The colour is a supporting cue, not the thing a reader is meant
// to decode — at 7px, `purpleMid` and `purpleSoft` are a family, not a code.
//
// If categories ever need to be told apart at a glance, that is a palette
// decision for `theme.js`, not seven one-off hexes here.

import { SP } from '../manuscript/structureTokens';

export { SP, FONT, BP, eyebrowSx } from '../manuscript/structureTokens';

// Display order for the filter row and the grid. Not alphabetical: the two
// categories most authors want are first, and the planning sheets — which are
// reference documents rather than books — are last.
export const TEMPLATE_CATEGORIES = [
  { id: 'fiction', label: 'Fiction' },
  { id: 'nonfiction', label: 'Non-Fiction' },
  { id: 'screenwriting', label: 'Screenwriting' },
  { id: 'stageplay', label: 'Stage' },
  { id: 'comic', label: 'Comics' },
  { id: 'academic', label: 'Academic' },
  { id: 'planning', label: 'Planning Sheets' },
];

export const CATEGORY_LABEL = Object.fromEntries(
  TEMPLATE_CATEGORIES.map((c) => [c.id, c.label])
);

/**
 * The categories present in a catalogue, in display order: the known ones
 * first, then any the frontend has never heard of.
 *
 * The trailing part is the point. The catalogue lives in Mongo precisely so it
 * can be changed without a deploy, which means a category can appear that this
 * file does not list. Filtering to `TEMPLATE_CATEGORIES` alone would drop those
 * templates out of the Create dialog's picker while the gallery grid still
 * showed them under "All templates" — a template you can see and cannot choose.
 * Falling back to the raw id as a label is worse than a nice name and far
 * better than a missing template.
 */
export function orderedCategoriesFor(templates) {
  const present = new Set((templates || []).map((t) => t.category).filter(Boolean));
  const known = TEMPLATE_CATEGORIES.filter((c) => present.has(c.id));
  const knownIds = new Set(TEMPLATE_CATEGORIES.map((c) => c.id));
  const unknown = [...present]
    .filter((id) => !knownIds.has(id))
    .sort()
    .map((id) => ({ id, label: id }));
  return [...known, ...unknown];
}

// Every value here is a token from structureTokens — nothing invented. See the
// note above on why these are a family rather than seven signals.
export const TEMPLATE_CATEGORY_COLOR = {
  fiction: SP.purple,
  nonfiction: SP.goldDeep,
  screenwriting: SP.purpleMid,
  stageplay: SP.purpleSoft,
  comic: SP.gold,
  academic: SP.purpleDeep,
  planning: SP.goldLight,
};

// The thumbnail ground. One tint for every category, deliberately: the
// prototype tinted each category's thumbnail its own colour, which needs the
// seven hues the app does not have. A single lavender ground lets the format
// mockup inside it be the thing that differs — which is what the thumbnail is
// actually for.
export const THUMB_BG = SP.lavender;

// Screenplays are written in Courier and a reader recognises the format by it,
// so the mockup would lose its point in Inter. This is the app's own stack from
// `src/config/fonts.js`, not a new webfont pulled in for a thumbnail.
export const COURIER = '"Courier New", Courier, "Andale Mono", monospace';
